import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAutomations,
  fetchAutomationOptions,
  automationCreatedRealtime,
  automationUpdatedRealtime,
  automationDeletedRealtime,
  automationToggledRealtime
} from '../../redux/Slices/automationSlice';
import { fetchBoardById } from '../../redux/Slices/boardSlice';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';
import AutomationList from './AutomationList';
import useBoardPermissions from '../../hooks/useBoardPermissions';
import './Automation.scss';

const AutomationPage = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { automations, automationUsage, loading, error } = useSelector((state) => state.automations);
  const { board } = useSelector((state) => state.board);
  const { isAdmin } = useBoardPermissions(board);

  const isUnlimited = automationUsage?.limit === null;
  const usagePercent = (!isUnlimited && automationUsage?.limit)
    ? Math.min((automationUsage.used / automationUsage.limit) * 100, 100)
    : 0;
  const isLimitReached = !isUnlimited && automationUsage && automationUsage.used >= automationUsage.limit;

  useEffect(() => {
    if (boardId) {
      dispatch(fetchBoardById(boardId));
      dispatch(fetchAutomations(boardId));
      dispatch(fetchAutomationOptions(boardId));
    }
  }, [boardId, dispatch]);

  // Real-time automation updates via socket
  useSocketAction([
    ActionTypes.AUTOMATION_CREATED,
    ActionTypes.AUTOMATION_UPDATED,
    ActionTypes.AUTOMATION_DELETED,
    ActionTypes.AUTOMATION_TOGGLED,
  ], (data) => {
    if (data.boardId !== boardId) return;

    switch (data.action) {
      case ActionTypes.AUTOMATION_CREATED:
        if (data.automation) dispatch(automationCreatedRealtime(data.automation));
        break;
      case ActionTypes.AUTOMATION_UPDATED:
        if (data.automation) dispatch(automationUpdatedRealtime(data.automation));
        break;
      case ActionTypes.AUTOMATION_DELETED:
        if (data.automationId) dispatch(automationDeletedRealtime(data.automationId));
        break;
      case ActionTypes.AUTOMATION_TOGGLED:
        if (data.automation) dispatch(automationToggledRealtime(data.automation));
        break;
      default:
        break;
    }
  }, [boardId, dispatch]);

  const handleBackToBoard = () => {
    navigate(`/b/${boardId}`);
  };

  const handleCreateRule = () => {
    navigate(`/b/${boardId}/automations/new`);
  };

  return (
    <div className="automation-page">
      <div className="automation-hero">
        <div className="hero-left">
          <div className="hero-icon-wrapper">
            <span className="material-symbols-outlined">bolt</span>
          </div>
          <div className="hero-text">
            <h1 className="hero-title">Automations{board?.title ? ` - ${board.title}` : ''}</h1>
            <p className="hero-subtitle">Put your workflow on autopilot. Create rules to automate repetitive tasks and save time.</p>
          </div>
        </div>

        <div className="hero-right">
          <button onClick={handleBackToBoard} className="hero-btn btn-secondary">
            <span className="material-symbols-outlined">arrow_back</span>
            Back to Board
          </button>
          {isAdmin && (
            <button onClick={handleCreateRule} className="hero-btn btn-primary">
              <span className="material-symbols-outlined">add</span>
              Create Rule
            </button>
          )}
        </div>
      </div>

      {automationUsage && (
        <div className={`automation-usage-bar ${isLimitReached ? 'limit-reached' : ''}`}>
          <div className="usage-info">
            <span className="material-symbols-outlined usage-icon">speed</span>
            <span className="usage-text">
              {isUnlimited ? (
                <>
                  <strong>{automationUsage.used}</strong> automation runs this month
                  {automationUsage.workspaceName && <span className="usage-workspace-name">in {automationUsage.workspaceName}</span>}
                  <span className="usage-plan-badge unlimited">Unlimited</span>
                </>
              ) : (
                <>
                  <strong>{automationUsage.used}</strong> / {automationUsage.limit} automation runs this month
                  {automationUsage.workspaceName && <span className="usage-workspace-name">in {automationUsage.workspaceName}</span>}
                  <span className="usage-plan-badge">{automationUsage.planType}</span>
                </>
              )}
            </span>
          </div>
          {!isUnlimited && (
            <div className="usage-progress-wrapper">
              <div className="usage-progress-track">
                <div
                  className={`usage-progress-fill ${usagePercent >= 90 ? 'critical' : usagePercent >= 70 ? 'warning' : ''}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
            </div>
          )}
          {isLimitReached && (
            <div className="usage-limit-warning">
              <span className="material-symbols-outlined">warning</span>
              <span>Monthly limit reached. Automations are paused until next month.</span>
              <a href={`/w/${automationUsage.workspaceShortId}/billing`} className="upgrade-link">Upgrade plan</a>
            </div>
          )}
        </div>
      )}

      <div className="automation-container">
        {loading ? (
          <div className="automation-loading">
            <div className="automation-skeleton" />
            <div className="automation-skeleton" />
            <div className="automation-skeleton" />
            <div className="automation-skeleton" />
            <div className="automation-skeleton" />
            <div className="automation-skeleton" />
          </div>
        ) : error ? (
          <div className="automation-error">
            <span className="material-symbols-outlined">error</span>
            <p>{error}</p>
          </div>
        ) : automations.length === 0 ? (
          <div className="automation-empty">
            <div className="empty-icon-wrapper">
              <span className="material-symbols-outlined empty-icon">auto_awesome</span>
            </div>
            <h2>No automations yet</h2>
            <p>{isAdmin
              ? 'Create your first automation rule to get started. Automations help you save time by automatically performing actions when certain conditions are met.'
              : 'No automation rules have been created for this board yet. Only board admins can create automations.'
            }</p>
            {isAdmin && (
              <button onClick={handleCreateRule} className="hero-btn btn-primary" style={{ margin: '0 auto' }}>
                <span className="material-symbols-outlined">add</span>
                Create Your First Rule
              </button>
            )}
          </div>
        ) : (
          <AutomationList automations={automations} boardId={boardId} isAdmin={isAdmin} />
        )}
      </div>
    </div>
  );
};

export default AutomationPage;
